/**
 * UsersPage — the /users route. Leadership-facing Cognito user directory management.
 *
 * Two tabs (Active & Invited / Deactivated), client-side free-text search (name + email), a
 * multi-select role filter, and sortable Name/Role/Status columns. Leadership can invite, edit,
 * reset passwords for, remove, and reactivate users. All mutations re-enforce RBAC server-side; the
 * UI hides actions for non-Leadership and self-removal.
 *
 * Access: the directory exposes every user's name, email and role, so non-Leadership/admin roles are
 * redirected to /projects rather than being shown a read-only copy of it (same pattern as
 * IntakesPage / IntakeWizardPage). The list endpoint re-enforces this server-side.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  Filter,
  KeyRound,
  MoreVertical,
  Pencil,
  RotateCcw,
  Search,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { useActivateUser, useManageUsers } from "@/hooks/useUsers";
import { cn } from "@/lib/utils";
import {
  describeUserCount,
  filterUsers,
  ROLE_FILTER_OPTIONS,
  sortUsers,
  TAB_NOUN,
  type UserSortDir,
  type UserSortKey,
  type UserTab,
} from "@/lib/user-directory";
import type {
  DeliverProUser,
  DeliverProUserRole,
  DeliverProUserStatus,
} from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge, type BadgeVariant } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs } from "@/components/ui/tabs";
import { Tooltip } from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AddUserModal } from "@/components/users/AddUserModal";
import { EditUserModal } from "@/components/users/EditUserModal";
import { DeactivateUserModal } from "@/components/users/DeactivateUserModal";
import { ResetPasswordModal } from "@/components/users/ResetPasswordModal";

const ROLE_VARIANT: Record<DeliverProUserRole, BadgeVariant> = {
  PM: "info",
  SA: "neutral",
  Leadership: "default",
};

const STATUS_META: Record<
  DeliverProUserStatus,
  { label: string; variant: BadgeVariant }
> = {
  active: { label: "Active", variant: "success" },
  invited: { label: "Invited", variant: "warning" },
  deactivated: { label: "Deactivated", variant: "neutral" },
};

/**
 * Tab hints. USR-02: the "active" tab queries `?enabled=true` → `WHERE is_active = true`, which
 * includes invited users (they are enabled, just pre-first-sign-in). That is intentional, so the tab
 * is named and described for what it actually contains rather than having the query narrowed.
 */
const TAB_TOOLTIP: Record<UserTab, string> = {
  active:
    "Users whose account is enabled — includes invited users who have not signed in and set a password yet.",
  deactivated: "Users whose account has been disabled. They cannot sign in.",
};

function RoleBadge({ role }: { role: DeliverProUserRole }): JSX.Element {
  return <Badge variant={ROLE_VARIANT[role]}>{role}</Badge>;
}

/** Shared client-side filter (free-text + role multi-select) plus the active column sort. */
function useVisibleUsers(
  users: DeliverProUser[],
  search: string,
  roleFilter: Set<DeliverProUserRole>,
  sortKey: UserSortKey,
  sortDir: UserSortDir,
): DeliverProUser[] {
  return useMemo(
    () => sortUsers(filterUsers(users, search, roleFilter), sortKey, sortDir),
    [users, search, roleFilter, sortKey, sortDir],
  );
}

/**
 * Sortable column header (USR-05). Same pattern as the Projects table: a real <button>, `aria-sort`
 * on the cell, a full-contrast arrow for the active direction, and an inactive hint revealed only on
 * hover/keyboard focus so the sorted column is the only indicator visible at rest.
 */
function SortHeader({
  label,
  column,
  sortKey,
  sortDir,
  onSort,
  className,
}: {
  label: string;
  column: UserSortKey;
  sortKey: UserSortKey;
  sortDir: UserSortDir;
  onSort: (key: UserSortKey) => void;
  className?: string;
}): JSX.Element {
  const active = sortKey === column;
  return (
    <TableHead
      className={cn("select-none", className)}
      aria-sort={
        active ? (sortDir === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className="group flex w-full items-center gap-1 text-left uppercase tracking-[0.48px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
      >
        <span className={cn(active && "text-text-primary")}>{label}</span>
        {active ? (
          sortDir === "asc" ? (
            <ArrowUp
              className="h-3.5 w-3.5 shrink-0 text-text-primary"
              strokeWidth={3}
              aria-hidden="true"
            />
          ) : (
            <ArrowDown
              className="h-3.5 w-3.5 shrink-0 text-text-primary"
              strokeWidth={3}
              aria-hidden="true"
            />
          )
        ) : (
          <ChevronsUpDown
            className="h-3 w-3 shrink-0 text-text-placeholder opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            aria-hidden="true"
          />
        )}
      </button>
    </TableHead>
  );
}

function LoadingRows(): JSX.Element {
  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </CardContent>
    </Card>
  );
}

function EmptyRow({
  colSpan,
  message,
}: {
  colSpan: number;
  message: string;
}): JSX.Element {
  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        className="py-10 text-center text-sm text-text-secondary"
      >
        {message}
      </TableCell>
    </TableRow>
  );
}

function UsersPage(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.role === "leadership" || user?.role === "admin";
  const currentEmail = user?.email?.trim().toLowerCase() ?? "";

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<Set<DeliverProUserRole>>(
    new Set(),
  );
  const [tab, setTab] = useState<UserTab>("active");
  const [sortKey, setSortKey] = useState<UserSortKey>("full_name");
  const [sortDir, setSortDir] = useState<UserSortDir>("asc");

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DeliverProUser | null>(null);
  const [deactivateTarget, setDeactivateTarget] =
    useState<DeliverProUser | null>(null);
  const [resetTarget, setResetTarget] = useState<DeliverProUser | null>(null);

  const activeQuery = useManageUsers(true);
  const deactivatedQuery = useManageUsers(false);
  const activateUser = useActivateUser();

  // The user directory is a Leadership surface — everyone else is sent back to /projects instead of
  // landing on a read-only view of every colleague's name, email and role.
  useEffect(() => {
    if (user && !canManage) navigate("/projects", { replace: true });
  }, [user, canManage, navigate]);

  const activeUsers = useVisibleUsers(
    activeQuery.data ?? [],
    search,
    roleFilter,
    sortKey,
    sortDir,
  );
  const deactivatedUsers = useVisibleUsers(
    deactivatedQuery.data ?? [],
    search,
    roleFilter,
    sortKey,
    sortDir,
  );

  const toggleRole = (role: DeliverProUserRole): void => {
    setRoleFilter((prev) => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  };

  const handleSort = (key: UserSortKey): void => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleReactivate = (u: DeliverProUser): void => {
    activateUser.mutate(
      { userId: u.id },
      {
        onSuccess: () => toast.success(`${u.full_name} has been reactivated`),
        onError: (err) =>
          toast.error(
            err instanceof Error ? err.message : "Failed to reactivate user",
          ),
      },
    );
  };

  // Self = same Cognito sub OR same (trimmed, case-insensitive) email — mirrors the backend guard
  // (packages/users/service.ts isSelf), which matches on either so a re-created Cognito account
  // (new sub, same email) is still recognised as the caller.
  const currentUserId = user?.userId ?? "";
  const isSelf = (u: DeliverProUser): boolean =>
    (currentEmail !== "" && u.email.trim().toLowerCase() === currentEmail) ||
    (currentUserId !== "" && u.cognito_sub === currentUserId);

  // USR-06: the badge used to show active + deactivated and never moved when the tab, search or role
  // filter changed. It now reports the rows actually rendered, with the full arithmetic spelled out
  // underneath so "which count is this?" has an on-screen answer.
  const activeTotal = activeQuery.data?.length ?? 0;
  const deactivatedTotal = deactivatedQuery.data?.length ?? 0;
  const directoryTotal = activeTotal + deactivatedTotal;
  const visibleUsers = tab === "active" ? activeUsers : deactivatedUsers;
  const tabTotal = tab === "active" ? activeTotal : deactivatedTotal;
  const countSummary = describeUserCount(
    tab,
    visibleUsers.length,
    tabTotal,
    directoryTotal,
  );
  const hasFilters = search.trim() !== "" || roleFilter.size > 0;

  // Render nothing while the redirect above is in flight — no directory data leaks to the DOM.
  if (!canManage) return <></>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-neutral-900">Users</h1>
            <Badge variant="neutral" title={countSummary}>
              {visibleUsers.length} shown
            </Badge>
          </div>
          <p className="text-sm text-neutral-500">{countSummary}</p>
        </div>
        {canManage && (
          /* USR-04: this is an invite flow, not a direct account creation — POST /api/users/manage
             runs Cognito AdminCreateUser with no MessageAction, so Cognito emails a temporary
             password and the account sits in FORCE_CHANGE_PASSWORD (= "Invited") until the user
             signs in. See packages/users/handlers/create-user.ts. */
          <Button className="h-12 gap-2" onClick={() => setAddOpen(true)}>
            <UserPlus />
            Invite User
          </Button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            aria-label="Search users"
            className="h-12 pl-9 pr-9 bg-white border-neutral-300"
          />
          {search && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-text-secondary"
            >
              <X />
            </Button>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="h-12 gap-2">
              <Filter className="h-4 w-4" />
              Role
              {roleFilter.size > 0 && (
                <Badge variant="neutral">{roleFilter.size}</Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          {/* USR-07: "All roles" first, then role names A–Z via ROLE_FILTER_OPTIONS. */}
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel>Filter by role</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={roleFilter.size === 0}
              onCheckedChange={() => setRoleFilter(new Set())}
              onSelect={(e) => e.preventDefault()}
            >
              All roles
            </DropdownMenuCheckboxItem>
            {ROLE_FILTER_OPTIONS.map((r) => (
              <DropdownMenuCheckboxItem
                key={r}
                checked={roleFilter.has(r)}
                onCheckedChange={() => toggleRole(r)}
                onSelect={(e) => e.preventDefault()}
              >
                {r}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {hasFilters && (
          <Button
            variant="ghost"
            className="gap-1.5"
            onClick={() => {
              setSearch("");
              setRoleFilter(new Set());
            }}
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}
      </div>

      {/* Error */}
      {(activeQuery.isError || deactivatedQuery.isError) && (
        <Alert variant="destructive">
          <AlertTitle>Failed to load users</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>The user directory could not be loaded.</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void activeQuery.refetch();
                void deactivatedQuery.refetch();
              }}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs — labels and counts state exactly what each tab contains (USR-02). */}
      <div>
        <Tabs
          active={tab}
          onChange={(v) => setTab(v as UserTab)}
          tabs={[
            {
              id: "active",
              label: "Active & Invited",
              count: String(activeUsers.length),
              tooltip: TAB_TOOLTIP.active,
            },
            {
              id: "deactivated",
              label: "Deactivated Users",
              count: String(deactivatedUsers.length),
              tooltip: TAB_TOOLTIP.deactivated,
            },
          ]}
        />

        {/* Active & Invited */}
        {tab === "active" && (
          <div className="mt-4">
            {activeQuery.isLoading ? (
              <LoadingRows />
            ) : (
              <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortHeader
                        label="Name"
                        column="full_name"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Role"
                        column="role"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Status"
                        column="status"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <TableHead className="w-24 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeUsers.length === 0 ? (
                      <EmptyRow
                        colSpan={4}
                        message={
                          hasFilters
                            ? "No matching users."
                            : `No ${TAB_NOUN.active} users.`
                        }
                      />
                    ) : (
                      activeUsers.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-text-primary">
                                {u.full_name}
                              </span>
                              <span className="text-xs text-text-secondary">
                                {u.email}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <RoleBadge role={u.role} />
                          </TableCell>
                          <TableCell>
                            <Badge variant={STATUS_META[u.status].variant}>
                              {STATUS_META[u.status].label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {/* USR-01/USR-03: one consolidated actions menu instead of a bare pencil
                                plus a three-dot menu. Visibility is unchanged — the whole control is
                                still gated on `canManage`, and Remove is still hidden for self.
                                Radix DropdownMenu supplies the full keyboard menu pattern
                                (arrows / Home / End / Enter / Escape / type-ahead); the trigger is a
                                42×42 icon Button with a name, a tooltip and a focus-visible ring. */}
                            {canManage && (
                              <div className="flex items-center justify-end">
                                <DropdownMenu>
                                  <Tooltip
                                    text={`User actions for ${u.full_name}`}
                                  >
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        aria-label={`User actions for ${u.full_name}`}
                                      >
                                        <MoreVertical className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                  </Tooltip>
                                  <DropdownMenuContent
                                    align="end"
                                    className="w-48"
                                  >
                                    <DropdownMenuLabel>
                                      {u.full_name}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onSelect={() => setEditTarget(u)}
                                    >
                                      <Pencil className="h-4 w-4" />
                                      Edit User
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onSelect={() => setResetTarget(u)}
                                    >
                                      <KeyRound className="h-4 w-4" />
                                      Reset Password
                                    </DropdownMenuItem>
                                    {!isSelf(u) && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          className="text-error focus:text-error"
                                          onSelect={() =>
                                            setDeactivateTarget(u)
                                          }
                                        >
                                          <Trash2 className="h-4 w-4" />
                                          Remove User
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {/* Deactivated Users */}
        {tab === "deactivated" && (
          <div className="mt-4">
            {deactivatedQuery.isLoading ? (
              <LoadingRows />
            ) : (
              <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortHeader
                        label="Name"
                        column="full_name"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Email"
                        column="email"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Role"
                        column="role"
                        sortKey={sortKey}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <TableHead className="w-32 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deactivatedUsers.length === 0 ? (
                      <EmptyRow
                        colSpan={4}
                        message={
                          hasFilters
                            ? "No matching users."
                            : "No deactivated users."
                        }
                      />
                    ) : (
                      deactivatedUsers.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium text-text-primary">
                            {u.full_name}
                          </TableCell>
                          <TableCell className="text-text-secondary">
                            {u.email}
                          </TableCell>
                          <TableCell>
                            <RoleBadge role={u.role} />
                          </TableCell>
                          <TableCell className="text-right">
                            {canManage && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-1.5"
                                onClick={() => handleReactivate(u)}
                                disabled={activateUser.isPending}
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                                Reactivate
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals (Leadership only) */}
      {canManage && (
        <>
          <AddUserModal open={addOpen} onOpenChange={setAddOpen} />
          {editTarget && (
            <EditUserModal
              key={editTarget.id}
              user={editTarget}
              open={editTarget !== null}
              onOpenChange={(o) => !o && setEditTarget(null)}
              /* Self-edit: name stays editable, Role is locked — mirrors the backend guard
                 (403 SELF_ROLE_CHANGE_FORBIDDEN in packages/users/handlers/update-user.ts). */
              isSelf={isSelf(editTarget)}
            />
          )}
          {deactivateTarget && (
            <DeactivateUserModal
              key={deactivateTarget.id}
              user={deactivateTarget}
              open={deactivateTarget !== null}
              onOpenChange={(o) => !o && setDeactivateTarget(null)}
            />
          )}
          {resetTarget && (
            <ResetPasswordModal
              key={resetTarget.id}
              user={resetTarget}
              open={resetTarget !== null}
              onOpenChange={(o) => !o && setResetTarget(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

export default UsersPage;
